import { useTranslation } from 'next-i18next';
import { pdf } from '@react-pdf/renderer';

import { isAssetDtoId, useAssets } from '@/entities/assets';
import { useAssetPdfData } from '@/entities/assets/hooks/useAssetPdfData';
import { isPersonalIdentifierDocumentDtoId, isPrivateDocumentDtoId } from '@/entities/document';
import { usePersonalIdPdfData } from '@/entities/personalIdentifiers/hooks/usePersonalIdPdfData';
import { usePrivateDocumentPdfData } from '@/entities/privateDocuments/hooks/usePrivateDocumentPdfData';
import { NAME_KEY_BY_ASSET_CATEGORY } from '@/shared/lib/sj-orm';
import { useSjActions } from '@/shared/lib/sj-orm/hooks/useSjActions';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { EntityPDF } from '@/shared/pdf-templates/EntityPDF';
import { EntityPdfData } from '@/shared/pdf-templates/EntityPDF/types';
import { I18nPdfProvider } from '@/shared/providers/I18nPdfProvider';

const EMPTY_TEXT = '-';

type GeneratedEntity = {
  file: File;
  title: string;
};

export const useGenerateEntityPdf = () => {
  const { i18n } = useTranslation('forms_assets');
  const { getAsset } = useAssets();
  const sjPrivateDocs = useSjActions('privateDocuments');
  const sjPersonalIds = useSjActions('personalIdentifiersDocuments');

  const { getEntityData: getAssetPdfData } = useAssetPdfData();
  const { getEntityData: getPrivateDocPdfData } = usePrivateDocumentPdfData();
  const { getEntityData: getPersonalIdPdfData } = usePersonalIdPdfData();

  const generateEntity = async (id: DtoId): Promise<GeneratedEntity | null> => {
    let data: EntityPdfData | null = null;
    let title = '';

    if (isAssetDtoId(id)) {
      const asset = getAsset(id);
      if (!asset) return null;
      data = await getAssetPdfData(asset);
      title = String(asset[NAME_KEY_BY_ASSET_CATEGORY[asset.category]]);
    }

    if (isPrivateDocumentDtoId(id)) {
      const privateDocument = sjPrivateDocs.findById(id);
      if (!privateDocument) return null;
      data = getPrivateDocPdfData(privateDocument);
      title = privateDocument.name;
    }

    if (isPersonalIdentifierDocumentDtoId(id)) {
      const personalId = sjPersonalIds.findById(id);
      if (!personalId) return null;
      data = getPersonalIdPdfData(personalId);
      title = personalId.name ?? EMPTY_TEXT;
    }

    if (!data) return null;
    const blob = await processPdf(data);
    const file = new File([blob], `${title}.pdf`, { type: 'application/pdf' });

    return { file, title };
  };

  const processPdf = async (data: EntityPdfData) => {
    const docPdf = pdf(
      <I18nPdfProvider i18n={i18n}>
        <EntityPDF data={data} />
      </I18nPdfProvider>,
    );

    const blob = await docPdf.toBlob();
    return blob;
  };

  return { generateEntity };
};
